import { useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import {
  ChevronLeft, ChevronRight, Plus, Clock, CheckCircle2,
  Users, FileText, Target, AlertTriangle, Calendar, Trash2, Link2, Bell
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Event color mapping - matches desktop
const EVENT_COLORS = {
  task: { pending: '#f59e0b', completed: '#10b981' },
  followup: '#8b5cf6',
  quote_deadline: '#ef4444',
  enquiry_deadline: '#ec4899'
};

// Category labels
const CATEGORY_LABELS = {
  outreach: 'Outreach',
  follow_up: 'Follow-up',
  visibility: 'Visibility',
  pipeline_hygiene: 'Pipeline'
};

// Get event color
const getEventColor = (event) => {
  if (event.type === 'task') {
    return event.status === 'completed' ? EVENT_COLORS.task.completed : EVENT_COLORS.task.pending;
  }
  return EVENT_COLORS[event.type] || '#6b7280';
};

// Get event type label
const getEventTypeLabel = (event) => {
  if (event.type === 'task') {
    return event.status === 'completed' ? 'Done' : 'Pending';
  }
  if (event.type === 'followup') return 'Follow-up';
  if (event.type === 'quote_deadline') return 'Quote Due';
  if (event.type === 'enquiry_deadline') return 'Deadline';
  return 'Event';
};

// Get event icon
const getEventIcon = (event) => {
  if (event.type === 'task') {
    return event.status === 'completed' ? CheckCircle2 : Target;
  }
  if (event.type === 'followup') return Users;
  if (event.type === 'quote_deadline') return FileText;
  if (event.type === 'enquiry_deadline') return AlertTriangle;
  return Calendar;
};

// Format time from ISO string
const formatTime = (dateStr) => {
  if (!dateStr) return 'All day';
  const date = new Date(dateStr);
  const hours = date.getHours();
  const minutes = date.getMinutes();
  if (hours === 0 && minutes === 0) return 'All day';
  return date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
};

// Format time range with duration
const formatTimeRange = (event) => {
  if (event.allDay) return 'All day';
  const startTime = formatTime(event.start);
  if (startTime === 'All day') return 'All day';
  
  const duration = event.duration_minutes || 60;
  const endTime = formatTime(event.end);
  
  // Format duration label
  let durationLabel = '';
  if (duration === 15) durationLabel = '15m';
  else if (duration === 30) durationLabel = '30m';
  else if (duration === 45) durationLabel = '45m';
  else if (duration === 60) durationLabel = '1h';
  else if (duration === 90) durationLabel = '1.5h';
  else if (duration === 120) durationLabel = '2h';
  else durationLabel = `${duration}m`;
  
  return { start: startTime, end: endTime, duration: durationLabel };
};

// Get week dates starting from given date's week
const getWeekDates = (date) => {
  const current = new Date(date);
  const dayOfWeek = current.getDay();
  const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
  const monday = new Date(current);
  monday.setDate(current.getDate() + mondayOffset);
  
  const weekDates = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    weekDates.push(d);
  }
  return weekDates;
};

// Format date for display
const formatMonthYear = (date) => {
  return date.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
};

// Check if two dates are the same day
const isSameDay = (date1, date2) => {
  return date1.getFullYear() === date2.getFullYear() &&
         date1.getMonth() === date2.getMonth() &&
         date1.getDate() === date2.getDate();
};

// Day abbreviations
const DAY_LETTERS = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];

export const MobileCalendarView = ({ 
  token, 
  onEventClick, 
  onCreateTask,
  onOpenSubscription,
  onOpenPreferences,
  _onEventsChange 
}) => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [weekDates, setWeekDates] = useState(getWeekDates(new Date()));
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [eventDotMap, setEventDotMap] = useState({});
  const [swipedEventId, setSwipedEventId] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  
  const today = new Date();

  // Handle quick delete from list
  const handleQuickDelete = async (event) => {
    if (event.type !== 'task') {
      toast.error('Can only delete tasks');
      return;
    }
    
    try {
      const res = await fetch(`${API_URL}/api/today/tasks/${event.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success('Task deleted');
        setDeleteConfirm(null);
        setSwipedEventId(null);
        // Refresh events immediately
        fetchEvents();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to delete task');
      }
    } catch (err) {
      toast.error('Error deleting task');
    }
  };

  // Handle create task - pass selected date
  const handleCreateTask = () => {
    onCreateTask(selectedDate.toISOString().split('T')[0]);
  };

  // Note: refreshEvents is available if parent needs to trigger refresh
  // eslint-disable-next-line no-unused-vars
  const refreshEvents = () => {
    fetchEvents();
  };

  // Fetch events for the visible range
  const fetchEvents = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    
    try {
      // Get start and end of week
      const startDate = weekDates[0];
      const endDate = new Date(weekDates[6]);
      endDate.setDate(endDate.getDate() + 1); // Include the last day
      
      const start = startDate.toISOString().split('T')[0];
      const end = endDate.toISOString().split('T')[0];
      
      const res = await fetch(
        `${API_URL}/api/calendar/events?start=${start}&end=${end}&include_daily_tasks=true`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (res.ok) {
        const data = await res.json();
        setEvents(data.events || []);
        
        // Build dot map for days with events
        const dotMap = {};
        (data.events || []).forEach(event => {
          const eventDate = event.start.split('T')[0];
          dotMap[eventDate] = true;
        });
        setEventDotMap(dotMap);
      }
    } catch (err) {
      console.error('Error fetching calendar events:', err);
    } finally {
      setLoading(false);
    }
  }, [token, weekDates]);

  // Expose fetchEvents for external refresh (only once on mount, not during render)
  // Note: _onEventsChange callback is intentionally NOT called during render
  // to avoid React "Cannot update component while rendering" errors

  // Fetch events when week changes
  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  // Update week dates when selected date changes
  useEffect(() => {
    setWeekDates(getWeekDates(selectedDate));
  }, [selectedDate]);

  // Navigate to previous week
  const goToPrevWeek = () => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() - 7);
    setSelectedDate(newDate);
  };

  // Navigate to next week
  const goToNextWeek = () => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + 7);
    setSelectedDate(newDate);
  };

  // Go to today
  const goToToday = () => {
    setSelectedDate(new Date());
  };

  // Get events for selected day
  const selectedDayEvents = events.filter(event => {
    const eventDate = event.start.split('T')[0];
    const selectedStr = selectedDate.toISOString().split('T')[0];
    return eventDate === selectedStr;
  }).sort((a, b) => {
    // Sort by time, all-day events first
    const timeA = a.start.includes('T') ? a.start : '00:00';
    const timeB = b.start.includes('T') ? b.start : '00:00';
    return timeA.localeCompare(timeB);
  });

  // Group events by time
  const groupedEvents = selectedDayEvents.reduce((groups, event) => {
    const time = formatTime(event.start);
    if (!groups[time]) groups[time] = [];
    groups[time].push(event);
    return groups;
  }, {});

  const timeSlots = Object.keys(groupedEvents).sort((a, b) => {
    if (a === 'All day') return -1;
    if (b === 'All day') return 1;
    return a.localeCompare(b);
  });

  return (
    <div className="flex flex-col h-full bg-slate-50" data-testid="mobile-calendar-view">
      {/* Header with Month/Year and Navigation */}
      <div className="bg-white border-b border-slate-100 px-4 py-3">
        <div className="flex items-center justify-between">
          <button 
            onClick={goToPrevWeek}
            className="p-2 -ml-2 rounded-xl hover:bg-slate-100 active:bg-slate-200 transition-colors"
            aria-label="Previous week"
          >
            <ChevronLeft className="w-5 h-5 text-slate-600" />
          </button>
          
          <div className="text-center">
            <h2 className="font-heading font-semibold text-[#1e3a5f]">
              {formatMonthYear(selectedDate)}
            </h2>
          </div>
          
          <div className="flex items-center gap-1">
            {onOpenPreferences && (
              <button 
                onClick={onOpenPreferences}
                className="p-2 rounded-xl hover:bg-slate-100 active:bg-slate-200 transition-colors"
                aria-label="Notification Preferences"
                data-testid="mobile-calendar-alerts-btn"
              >
                <Bell className="w-5 h-5 text-slate-600" />
              </button>
            )}
            {onOpenSubscription && (
              <button 
                onClick={onOpenSubscription}
                className="p-2 rounded-xl hover:bg-slate-100 active:bg-slate-200 transition-colors"
                aria-label="Export/Sync Calendar"
                data-testid="mobile-calendar-export-btn"
              >
                <Link2 className="w-5 h-5 text-blue-600" />
              </button>
            )}
            <button 
              onClick={goToNextWeek}
              className="p-2 -mr-2 rounded-xl hover:bg-slate-100 active:bg-slate-200 transition-colors"
              aria-label="Next week"
            >
              <ChevronRight className="w-5 h-5 text-slate-600" />
            </button>
          </div>
        </div>
        
        {/* Week Strip */}
        <div className="mt-4 grid grid-cols-7 gap-1">
          {weekDates.map((date, idx) => {
            const isSelected = isSameDay(date, selectedDate);
            const isToday = isSameDay(date, today);
            const dateStr = date.toISOString().split('T')[0];
            const hasEvents = eventDotMap[dateStr];
            
            return (
              <button
                key={idx}
                onClick={() => setSelectedDate(new Date(date))}
                className={`flex flex-col items-center py-2 rounded-xl transition-all ${
                  isSelected 
                    ? 'bg-[#1e3a5f] text-white shadow-lg' 
                    : isToday
                      ? 'bg-blue-50 text-[#1e3a5f]'
                      : 'text-slate-600 hover:bg-slate-50'
                }`}
                data-testid={`day-${dateStr}`}
              >
                <span className={`text-xs font-medium ${isSelected ? 'text-white/80' : 'text-slate-400'}`}>
                  {DAY_LETTERS[idx]}
                </span>
                <span className={`text-lg font-semibold mt-0.5 ${
                  isSelected ? 'text-white' : isToday ? 'text-[#1e3a5f]' : ''
                }`}>
                  {date.getDate()}
                </span>
                {/* Event dot indicator */}
                <div className={`w-1.5 h-1.5 rounded-full mt-1 ${
                  hasEvents 
                    ? isSelected 
                      ? 'bg-white' 
                      : 'bg-[#1e3a5f]'
                    : 'bg-transparent'
                }`} />
              </button>
            );
          })}
        </div>
      </div>

      {/* Actions Bar */}
      <div className="bg-white border-b border-slate-100 px-4 py-2 flex items-center justify-between">
        <button
          onClick={goToToday}
          className="text-sm font-medium text-[#1e3a5f] px-3 py-1.5 rounded-lg hover:bg-slate-100 active:bg-slate-200 transition-colors"
          data-testid="today-btn"
        >
          Today
        </button>
        
        <Button
          onClick={handleCreateTask}
          size="sm"
          className="bg-[#1e3a5f] hover:bg-[#162c46] text-white rounded-xl shadow-sm"
          data-testid="new-task-btn-mobile"
        >
          <Plus className="w-4 h-4 mr-1.5" />
          New Task
        </Button>
      </div>

      {/* Legend */}
      <div className="bg-white px-4 py-2 flex items-center gap-3 overflow-x-auto border-b border-slate-100">
        <div className="flex items-center gap-1.5 text-xs whitespace-nowrap">
          <div className="w-2 h-2 rounded-full bg-amber-500" />
          <span className="text-slate-500">Pending</span>
        </div>
        <div className="flex items-center gap-1.5 text-xs whitespace-nowrap">
          <div className="w-2 h-2 rounded-full bg-emerald-500" />
          <span className="text-slate-500">Done</span>
        </div>
        <div className="flex items-center gap-1.5 text-xs whitespace-nowrap">
          <div className="w-2 h-2 rounded-full bg-violet-500" />
          <span className="text-slate-500">Follow-up</span>
        </div>
        <div className="flex items-center gap-1.5 text-xs whitespace-nowrap">
          <div className="w-2 h-2 rounded-full bg-red-500" />
          <span className="text-slate-500">Quote</span>
        </div>
        <div className="flex items-center gap-1.5 text-xs whitespace-nowrap">
          <div className="w-2 h-2 rounded-full bg-pink-500" />
          <span className="text-slate-500">Deadline</span>
        </div>
      </div>

      {/* Agenda List */}
      <div className="flex-1 overflow-y-auto px-4 py-3" data-testid="agenda-list">
        {/* Selected Date Header */}
        <div className="mb-3">
          <h3 className="font-medium text-slate-800">
            {selectedDate.toLocaleDateString('en-GB', { 
              weekday: 'long', 
              day: 'numeric', 
              month: 'long' 
            })}
          </h3>
          <p className="text-xs text-slate-400">
            {selectedDayEvents.length} event{selectedDayEvents.length !== 1 ? 's' : ''}
          </p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-6 h-6 border-2 border-[#1e3a5f] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : selectedDayEvents.length === 0 ? (
          /* Empty State */
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mb-4">
              <Calendar className="w-8 h-8 text-slate-300" />
            </div>
            <p className="font-medium text-slate-500">No events</p>
            <p className="text-sm text-slate-400 mt-1">
              Nothing scheduled for this day
            </p>
            <Button
              onClick={handleCreateTask}
              variant="outline"
              size="sm"
              className="mt-4 rounded-xl border-slate-200"
            >
              <Plus className="w-4 h-4 mr-1.5" />
              Add Task
            </Button>
          </div>
        ) : (
          /* Event List - Time Rail Design */
          <div className="relative">
            {/* Time Rail - vertical line */}
            <div className="absolute left-[52px] top-0 bottom-0 w-px bg-slate-200" />
            
            <div className="space-y-1">
              {timeSlots.map((time, _slotIdx) => (
                <div key={time} className="relative">
                  {/* Time marker */}
                  <div className="flex items-start">
                    {/* Time Label */}
                    <div className="w-14 flex-shrink-0 pt-3 pr-2 text-right">
                      <span className="text-xs font-semibold text-[#1e3a5f]">
                        {time}
                      </span>
                    </div>
                    
                    {/* Time dot on rail */}
                    <div className="w-3 flex-shrink-0 flex justify-center pt-4">
                      <div className="w-2 h-2 rounded-full bg-[#1e3a5f] z-10" />
                    </div>
                    
                    {/* Events for this time slot */}
                    <div className="flex-1 pl-3 space-y-2 pb-3">
                      {groupedEvents[time].map(event => {
                        const EventIcon = getEventIcon(event);
                        const color = getEventColor(event);
                        const label = getEventTypeLabel(event);
                        const timeInfo = formatTimeRange(event);
                        const isTask = event.type === 'task';
                        const isSwiped = swipedEventId === event.id;
                        
                        return (
                          <div 
                            key={event.id} 
                            className="relative overflow-hidden rounded-xl"
                            data-testid={`event-card-${event.id}`}
                          >
                            {/* Delete button revealed on swipe */}
                            {isTask && (
                              <div 
                                className={`absolute inset-y-0 right-0 w-20 bg-red-500 flex items-center justify-center transition-opacity duration-200 ${
                                  isSwiped ? 'opacity-100' : 'opacity-0 pointer-events-none'
                                }`}
                              >
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setDeleteConfirm(event);
                                  }}
                                  className="w-full h-full flex flex-col items-center justify-center text-white"
                                  data-testid={`delete-btn-${event.id}`}
                                >
                                  <Trash2 className="w-5 h-5" />
                                  <span className="text-xs mt-1">Delete</span>
                                </button>
                              </div>
                            )}
                            
                            {/* Event Card */}
                            <div
                              className={`relative bg-white border border-slate-100 p-3 shadow-sm transition-transform duration-200 ${
                                isSwiped ? '-translate-x-20' : 'translate-x-0'
                              }`}
                              style={{ borderLeft: `3px solid ${color}` }}
                              onClick={() => {
                                if (isSwiped) {
                                  setSwipedEventId(null);
                                } else {
                                  onEventClick(event);
                                }
                              }}
                              onTouchStart={(e) => {
                                if (!isTask) return;
                                const touch = e.touches[0];
                                e.currentTarget.dataset.startX = touch.clientX;
                              }}
                              onTouchMove={(e) => {
                                if (!isTask) return;
                                const touch = e.touches[0];
                                const startX = parseFloat(e.currentTarget.dataset.startX || '0');
                                const diff = startX - touch.clientX;
                                if (diff > 50) {
                                  setSwipedEventId(event.id);
                                } else if (diff < -30) {
                                  setSwipedEventId(null);
                                }
                              }}
                            >
                              <div className="flex items-start gap-2">
                                {/* Status icon */}
                                <div 
                                  className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
                                  style={{ backgroundColor: `${color}15` }}
                                >
                                  <EventIcon className="w-3.5 h-3.5" style={{ color }} />
                                </div>
                                
                                {/* Content */}
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-1.5 mb-0.5">
                                    <span 
                                      className="text-[10px] font-bold px-1.5 py-0.5 rounded-full uppercase"
                                      style={{ 
                                        backgroundColor: `${color}15`,
                                        color: color
                                      }}
                                    >
                                      {label}
                                    </span>
                                    {event.category && event.type === 'task' && (
                                      <span className="text-[10px] text-slate-400">
                                        {CATEGORY_LABELS[event.category] || event.category}
                                      </span>
                                    )}
                                  </div>
                                  <p className="font-medium text-sm text-slate-800 truncate">
                                    {event.title}
                                  </p>
                                  {/* Time range with duration */}
                                  {typeof timeInfo === 'object' && (
                                    <p className="text-[11px] text-slate-500 mt-0.5 flex items-center gap-1">
                                      <Clock className="w-3 h-3" />
                                      {timeInfo.start} — {timeInfo.end}
                                      <span className="text-slate-400 font-medium">({timeInfo.duration})</span>
                                    </p>
                                  )}
                                  {/* Secondary info */}
                                  {event.contact_name && (
                                    <p className="text-[11px] text-slate-400 mt-0.5 truncate">
                                      {event.contact_name}
                                      {event.company_name && ` • ${event.company_name}`}
                                    </p>
                                  )}
                                  {/* Creator attribution */}
                                  {event.created_by && (
                                    <p className="text-[10px] text-slate-400 mt-0.5 flex items-center gap-1">
                                      <Users className="w-3 h-3" />
                                      {event.created_by}
                                    </p>
                                  )}
                                </div>
                                
                                {/* Quick delete icon for non-swiped state */}
                                {isTask && !isSwiped && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setDeleteConfirm(event);
                                    }}
                                    className="p-1.5 rounded-lg hover:bg-red-50 text-slate-300 hover:text-red-500 transition-colors"
                                    data-testid={`quick-delete-${event.id}`}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      
      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-5 w-full max-w-sm shadow-xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center">
                <Trash2 className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-900">Delete Task?</h3>
                <p className="text-sm text-slate-500">This cannot be undone</p>
              </div>
            </div>
            <p className="text-sm text-slate-600 mb-4 p-3 bg-slate-50 rounded-xl">
              "{deleteConfirm.title}"
            </p>
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1 rounded-xl"
                onClick={() => {
                  setDeleteConfirm(null);
                  setSwipedEventId(null);
                }}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                className="flex-1 rounded-xl bg-red-600 hover:bg-red-700"
                onClick={() => handleQuickDelete(deleteConfirm)}
              >
                Delete
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MobileCalendarView;
